import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { ActivatedRoute } from '@angular/router'
import { Store } from '@ngrx/store'
import * as moment from 'moment'
import { Editor, Toolbar, Validators as NgxValidators } from 'ngx-editor'
import { Observable } from 'rxjs'
import { GlobalConstants } from 'src/app/constants/global-constants'
import {
  AnnouncementAPIModel,
  CategorySelection,
  PaginatedAPI,
  SelectedCompanyAPIModel
} from 'src/app/models/api.model'
import { AnnouncementService } from 'src/app/services/announcement/announcement.service'
import { CommonService, formatDatetoYYYYMMDD } from 'src/app/services/common/common.service'
import { getAllAnnouncementSummary } from 'src/app/store/selector/announcement.selector'
import { AppState } from 'src/app/store/state/app.state'
import { addFormControl, removeFormControl } from 'src/app/utils/form-control.util'

@Component({
  selector: 'app-new-announcement',
  templateUrl: './new-announcement.component.html',
  styleUrls: ['./new-announcement.component.scss']
})
export class NewAnnouncementComponent implements OnInit, OnDestroy {
  @ViewChild('fileInput') fileInput: any
  editor: Editor
  toolbar: Toolbar = [
    // default value
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['code', 'blockquote'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
    ['link', 'image'],
    ['text_color', 'background_color'],
    ['align_left', 'align_center', 'align_right', 'align_justify']
  ]
  colorPresets = ['red', 'green', 'blue', 'grey', 'black']
  createAnnouncementForm: FormGroup
  announcementId: AnnouncementAPIModel
  announcementDetail: AnnouncementAPIModel
  announcementAllList$: Observable<PaginatedAPI<AnnouncementAPIModel>>
  filesToBeUploaded: File[] = []
  categoryTypes = ['NOTIFICATION', 'CIRCULAR']
  maxFileSizeAllowedInBytes = 5242880
  acceptedFormatMsg = `Accepted File Formats: PDF,JPEG,PNG,WORD,EML;<br>
  Max Size: ${this.maxFileSizeAllowedInBytes / (1024 * 1024)} MB;`
  showError: boolean = false
  errorMsg = `Please select only JPEG, PNG, PDF, EXCEL, DOCX files with a maximum size of ${
    this.maxFileSizeAllowedInBytes / (1024 * 1024)
  }MB.`
  readonly inputAccept = '.jpeg, .jpg, .png, .pdf, .doc, .docx, .eml'
  readonly DISPLAY_FROM_DATETIME_CONTROL_NAME = 'displayFromDatetime'
  readonly DISPLAY_TO_DATETIME_CONTROL_NAME = 'displayToDatetime'

  addDisplayFromToForm = addFormControl(this.DISPLAY_FROM_DATETIME_CONTROL_NAME, '', [Validators.required])
  removeDisplayFrom = removeFormControl(this.DISPLAY_FROM_DATETIME_CONTROL_NAME)
  addDisplayToToForm = addFormControl(this.DISPLAY_TO_DATETIME_CONTROL_NAME, '', [Validators.required])
  removeDisplayToFromForm = removeFormControl(this.DISPLAY_TO_DATETIME_CONTROL_NAME)

  showValidationErrors = false

  constructor(
    private formBuilder: FormBuilder,
    private announcementService: AnnouncementService,
    private cs: CommonService,
    private route: ActivatedRoute,
    private appState: Store<AppState>
  ) {}

  ngOnInit() {
    this.buildCreateAnnouncementForm()
    this.announcementAllList$ = this.appState.select(getAllAnnouncementSummary())
    this.editor = new Editor()
    this.route.params.subscribe(params => {
      const id = params['id']
      this.announcementId = id
    })
    if (this.announcementId) {
      this.dispatchFetchAnnouncementDetailsAction(this.announcementId)
      this.getAnnouncementDetailsFromStore()
      //Remove TaskCategory And TaskSubCategory in edit mode
      removeFormControl('taskCategory')(this.createAnnouncementForm)
      removeFormControl('taskSubCategory')(this.createAnnouncementForm)
    }
  }

  dispatchFetchAnnouncementDetailsAction(id: AnnouncementAPIModel) {
    this.announcementService.dispatchFetchAnnouncementDetailsAction(id)
  }

  handleFileInput(files: FileList) {
    this.showError = false
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'message/rfc822'
    ]

    const isValidFile = allowedTypes.includes(files[0].type) && files[0].size <= this.maxFileSizeAllowedInBytes
    if (!isValidFile) {
      this.showError = true
      return
    }
    this.filesToBeUploaded = Array.from(files)
  }

  announcementFormPatch(data) {
    this.createAnnouncementForm.patchValue({
      heading: data.heading,
      description: data.description,
      [this.DISPLAY_FROM_DATETIME_CONTROL_NAME]: formatDatetoYYYYMMDD(data.displayFromDatetime),
      [this.DISPLAY_TO_DATETIME_CONTROL_NAME]: formatDatetoYYYYMMDD(data.displayToDatetime),
      type: data.type
    })
  }

  getAnnouncementDetailsFromStore() {
    this.announcementService.getAnnouncementDetailsFromStore().subscribe(announcement => {
      if (announcement) {
        this.announcementDetail = announcement
        this.announcementFormPatch(this.announcementDetail)
      }
    })
  }

  ngOnDestroy() {
    this.editor.destroy()
  }

  buildCreateAnnouncementForm() {
    this.createAnnouncementForm = this.formBuilder.group({
      heading: ['', [Validators.required]],
      description: ['', [NgxValidators.required, NgxValidators.minLength(5), NgxValidators.maxLength(5000)]],
      [this.DISPLAY_FROM_DATETIME_CONTROL_NAME]: ['', [Validators.required]],
      [this.DISPLAY_TO_DATETIME_CONTROL_NAME]: ['', [Validators.required]],
      companyReferenceId: ['', Validators.required],
      companyReferenceType: ['', Validators.required],
      type: ['', [Validators.required]],
      taskCategory: ['', [Validators.required]],
      taskSubCategory: ['', [Validators.required]]
    })
  }

  handleCompanySelectedValue(company: SelectedCompanyAPIModel) {
    this.createAnnouncementForm.patchValue({ companyReferenceId: company?.id, companyReferenceType: company?.type })
  }

  get f() {
    return this.createAnnouncementForm.controls
  }

  saveAndExit() {
    if (this.createAnnouncementForm.invalid) {
      this.createAnnouncementForm.markAllAsTouched()
      this.showValidationErrors = true
      return
    }
    if (this.createAnnouncementForm.valid) {
      const files: File[] = this.filesToBeUploaded
      let announcement = this.createAnnouncementForm.value
      if (announcement.type === 'CIRCULAR') {
        announcement = {
          ...announcement,
          [this.DISPLAY_FROM_DATETIME_CONTROL_NAME]: moment(new Date()).format('YYYY-MM-DD'),
          [this.DISPLAY_TO_DATETIME_CONTROL_NAME]: '2099-12-31'
        }
      }
      if (this.announcementId) {
        this.announcementService.dispatchUpdateAnnouncementAction(
          this.announcementId,
          announcement,
          files,
          this.cs.createRoutingURL([GlobalConstants.ROUTING.ANNOUNCEMENT, GlobalConstants.ROUTING.SUMMARY])
        )
      } else {
        this.announcementService.dispatchSaveAnnouncementAction(
          announcement,
          files,
          this.cs.createRoutingURL([GlobalConstants.ROUTING.ANNOUNCEMENT, GlobalConstants.ROUTING.SUMMARY])
        )
      }
    }
  }

  exit() {
    this.cs.route(GlobalConstants.ROUTING.ANNOUNCEMENT, GlobalConstants.ROUTING.SUMMARY)
  }

  dateCheckFromAndTo() {
    const fromDate = new Date(this.createAnnouncementForm.get('displayFromDatetime').value)
    const tillDate = new Date(this.createAnnouncementForm.get('displayToDatetime').value)
    const today = new Date()

    const fromDateWithoutTime = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate())
    const tillDateWithoutTime = new Date(tillDate.getFullYear(), tillDate.getMonth(), tillDate.getDate())
    const todayWithoutTime = new Date(today.getFullYear(), today.getMonth(), today.getDate())

    if (todayWithoutTime > fromDateWithoutTime) {
      this.cs.showError('Display from -Must not be before the current date.')
      this.createAnnouncementForm.controls['displayFromDatetime'].reset()
    } else if (todayWithoutTime > tillDateWithoutTime) {
      this.cs.showError('Display till -Must not be before the current date.')
      this.createAnnouncementForm.controls['displayToDatetime'].reset()
    } else if (fromDateWithoutTime > tillDateWithoutTime) {
      this.cs.showError('The "from" date is after the "till" date.')
      this.createAnnouncementForm.controls['displayToDatetime'].reset()
    }
  }

  onCategoryChange(category: string) {
    if (category === 'CIRCULAR') {
      this.removeDisplayFrom(this.createAnnouncementForm)
      this.removeDisplayToFromForm(this.createAnnouncementForm)
    } else {
      this.addDisplayFromToForm(this.createAnnouncementForm)
      this.addDisplayToToForm(this.createAnnouncementForm)
    }
  }

  onCategorySelected(categorySelection: CategorySelection) {
    const { selectedCategory, selectedSubCategory } = categorySelection
    if (selectedCategory && selectedSubCategory) {
      this.createAnnouncementForm.patchValue({
        taskCategory: selectedCategory,
        taskSubCategory: selectedSubCategory
      })
    }
  }
}
